import { useEffect, useMemo, useState } from 'react';
import { MessageCircle, Play } from 'lucide-react';
import { useFiloRemote } from '@/connection/FiloRemoteContext';
import {
  DEFAULT_SAY_TEXT_SPEAKER,
  SAY_TEXT_VOICES,
} from '@/flow/config/sayTextVoice';
import { unlockSpeechAudioContext } from '@/speech/speechAudioContext';

export type ChatAppLauncherProps = {
  onLaunch: (robotId: string, speaker: number) => void;
};

export function ChatAppLauncher({ onLaunch }: ChatAppLauncherProps) {
  const remote = useFiloRemote();
  const connectedRobots = useMemo(() => (
    remote.robots.filter(({ robotId }) => (
      remote.snapshot.sessions[robotId]?.state === 'connected'
    ))
  ), [remote.robots, remote.snapshot.sessions]);
  const [selectedRobotId, setSelectedRobotId] = useState('');
  const [selectedSpeaker, setSelectedSpeaker] = useState(DEFAULT_SAY_TEXT_SPEAKER);
  const launchLocked = (
    remote.snapshot.isRunInProgress
    || remote.snapshot.isConnectionChangeInProgress
  );

  useEffect(() => {
    if (connectedRobots.some(({ robotId }) => robotId === selectedRobotId)) return;
    setSelectedRobotId(connectedRobots[0]?.robotId ?? '');
  }, [connectedRobots, selectedRobotId]);

  return (
    <section className="apps-panel" aria-labelledby="chat-app-panel-title">
      <article className="app-card">
        <div className="app-card__icon" aria-hidden="true">
          <MessageCircle size={27} />
        </div>
        <div className="app-card__content">
          <h3 id="chat-app-panel-title">おしゃべり</h3>
          <p>ロボットがかおを見つけて、声をききながらおしゃべりします。</p>
        </div>
        {connectedRobots.length > 1 && (
          <label className="app-card__select">
            ロボット
            <select
              value={selectedRobotId}
              onChange={(event) => setSelectedRobotId(event.target.value)}
            >
              {connectedRobots.map((robot) => (
                <option key={robot.robotId} value={robot.robotId}>
                  {robot.displayName}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="app-card__select">
          声の種類
          <select
            value={selectedSpeaker}
            onChange={(event) => setSelectedSpeaker(Number(event.target.value))}
          >
            {SAY_TEXT_VOICES.map((voice) => (
              <option key={voice.speaker} value={voice.speaker}>{voice.label}</option>
            ))}
          </select>
        </label>
        {connectedRobots.length === 0 && (
          <p className="app-card__unavailable">接続済みのロボットがありません</p>
        )}
        {launchLocked && (
          <p className="app-card__unavailable">
            フロー実行または接続変更が終わってから起動してください
          </p>
        )}
        <button
          type="button"
          className="app-card__launch"
          disabled={!selectedRobotId || launchLocked}
          onClick={() => {
            unlockSpeechAudioContext();
            onLaunch(selectedRobotId, selectedSpeaker);
          }}
        >
          <Play aria-hidden="true" size={16} fill="currentColor" />
          起動
        </button>
      </article>
    </section>
  );
}
